import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BookOpen, Compass, Heart, Mountain } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PhoneMockup } from "@/components/PhoneMockup";
import { ComingSoonButton } from "@/components/ComingSoonButton";
import { SEO } from "@/components/SEO";

const HEADLINE_LINES = [
  "A quiet place to spend",
  "a moment in Scripture,",
  "reflect on God’s promises,",
  "and pray.",
];

const FEATURES = [
  {
    icon: BookOpen,
    title: "Daily Promises",
    body: "A new Bible promise and prayer every day.",
  },
  {
    icon: Compass,
    title: "Find What You Are Facing",
    body: "Browse by category or emotion.",
  },
  {
    icon: Heart,
    title: "Save & Share",
    body: "Save your favorites and share with others.",
  },
  {
    icon: Mountain,
    title: "Be Encouraged",
    body: "Beautiful, cinematic visuals with peaceful audio.",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src="/assets/hero-mountains.webp"
          alt="Sunrise over misty mountain ridges"
          className="h-[115%] w-full object-cover"
        />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-start gap-16 px-6 pb-28 pt-28 sm:pt-32 lg:grid-cols-[1.05fr_0.95fr] lg:px-10 lg:pb-36 lg:pt-40">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#16233B]/60"
          >
            Daily Bible Promises &amp; Prayer.
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="mt-9 font-serif text-4xl font-normal leading-[1.14] tracking-tight text-[#16233B] sm:text-5xl lg:text-[3.7rem]"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="-mb-2 block overflow-hidden pb-3">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.28 + i * 0.13, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-brand-statement"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 font-serif text-xl text-[#16233B] [text-shadow:0_1px_24px_rgba(251,249,245,0.9),0_0_8px_rgba(251,249,245,0.7)] sm:text-2xl"
          >
            All God’s promises are Yes &amp; Amen.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10"
          >
            <ComingSoonButton testId="hero-appstore-button" />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 70 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.3, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          className="mx-auto w-60 sm:w-72 lg:w-[330px]"
        >
          <PhoneMockup
            src="/assets/screen-peace.webp"
            alt="YES & AMEN app showing the daily promise of peace from Isaiah 26:3"
            testId="hero-iphone-mockup"
            eager
          />
        </motion.div>
      </div>
    </section>
  );
};

const Features = () => (
  <section data-testid="features-section" className="bg-[#FBF9F5]">
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid gap-14 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-[#16233B]/10">
        {FEATURES.map((feature, i) => (
          <Reveal key={feature.title} delay={0.08 + i * 0.1} className="lg:px-10 lg:first:pl-0 lg:last:pr-0">
            <div data-testid={`feature-card-${i + 1}`}>
              <feature.icon className="h-7 w-7 text-[#16233B]" strokeWidth={1.25} />
              <h3 className="mt-7 text-[11px] font-semibold uppercase leading-relaxed tracking-[0.24em] text-[#16233B]">
                {feature.title}
              </h3>
              <p className="mt-3.5 max-w-[240px] text-sm leading-relaxed text-[#16233B]/55">
                {feature.body}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const Foundation = () => (
  <section data-testid="foundation-section" className="relative overflow-hidden">
    <motion.img
      src="/assets/ocean.webp"
      alt="Calm open ocean beneath a pale sky"
      loading="lazy"
      initial={{ scale: 1.08 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-white/10" />
    <div className="relative mx-auto max-w-4xl px-6 py-32 text-center md:py-44">
      <Reveal>
        <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-[#16233B]/55">
          Our Foundation
        </p>
      </Reveal>
      <Reveal delay={0.08}>
        <h2 className="mt-9 font-serif text-3xl font-normal tracking-tight text-[#16233B] sm:text-4xl lg:text-[2.9rem]">
          All God’s promises are Yes &amp; Amen.
        </h2>
      </Reveal>
      <Reveal delay={0.16}>
        <blockquote
          data-testid="scripture-quote"
          className="mx-auto mt-11 max-w-2xl font-serif text-lg leading-relaxed text-[#16233B]/80 sm:text-xl"
        >
          “For no matter how many promises God has made, they are ‘Yes’ in Christ. And so through
          him the ‘Amen’ is spoken by us to the glory of God.”
        </blockquote>
      </Reveal>
      <Reveal delay={0.24}>
        <p
          data-testid="scripture-reference"
          className="mt-9 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#16233B] [text-shadow:0_1px_18px_rgba(251,249,245,0.95),0_0_8px_rgba(251,249,245,0.8)]"
        >
          2 Corinthians 1:20
        </p>
      </Reveal>
    </div>
  </section>
);

const AppExperience = () => (
  <section data-testid="app-experience-section" className="overflow-hidden bg-[#FBF9F5]">
    <div className="mx-auto grid max-w-7xl items-center gap-20 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-36">
      <div>
        <Reveal>
          <p className="text-[11px] font-semibold uppercase leading-loose tracking-[0.35em] text-[#16233B]/50">
            Real Promises.
            <br />
            Real Prayers. Every Day.
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-9 font-serif text-3xl font-normal leading-[1.15] tracking-tight text-[#16233B] sm:text-4xl lg:text-[2.9rem]">
            New Promise &amp;
            <br />
            Prayer every day.
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 max-w-md text-base leading-relaxed text-[#16233B]/60">
            Find peace, hope, guidance and more through the promises of God and respond to Him in
            prayer.
          </p>
        </Reveal>
        <Reveal delay={0.24} className="mt-11">
          <ComingSoonButton testId="app-experience-appstore-button" />
        </Reveal>
      </div>

      <div className="relative flex items-end justify-center">
        <Reveal delay={0.18} className="relative z-0 -mr-8 w-32 sm:-mr-14 sm:w-56">
          <PhoneMockup
            src="/assets/screen-hope.webp"
            alt="YES & AMEN app promise of hope from Romans 15:13 over a sunset ocean"
            testId="app-mockup-hope"
            floatDelay={0.8}
          />
        </Reveal>
        <Reveal delay={0.02} className="relative z-10 w-40 sm:w-64">
          <PhoneMockup
            src="/assets/screen-anxiety.webp"
            alt="YES & AMEN app promise for anxiety from 1 Peter 5:7 over dark waves"
            testId="app-mockup-anxiety"
            floatDelay={0}
          />
        </Reveal>
        <Reveal delay={0.34} className="relative z-0 -ml-8 w-32 sm:-ml-14 sm:w-56">
          <PhoneMockup
            src="/assets/screen-guidance.webp"
            alt="YES & AMEN app promise of guidance from Proverbs 3:5 over misty mountains"
            testId="app-mockup-guidance"
            floatDelay={1.6}
          />
        </Reveal>
      </div>
    </div>
  </section>
);

export default function Home() {
  return (
    <main>
      <SEO
        title="YES & AMEN | Bible Promises & Prayer"
        description="A quiet place to spend a moment in Scripture, reflect on God’s promises, and pray. Discover a new Bible Promise & Prayer every day."
      />
      <Hero />
      <Features />
      <Foundation />
      <AppExperience />
    </main>
  );
}
